/**
 * Meeting Room - Video, voice, and chat
 */

import { useState } from 'react'
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  Phone,
  MessageSquare,
  Send,
  ScreenShare,
} from 'lucide-react'
import { cn } from '../lib/utils'

interface Participant {
  id: string
  name: string
  avatar: string
  isSpeaking: boolean
  videoOn: boolean
  audioOn: boolean
}

interface ChatMessage {
  id: string
  sender: string
  text: string
  time: string
}

const PARTICIPANTS: Participant[] = [
  { id: '1', name: 'Alex Chen', avatar: 'AC', isSpeaking: true, videoOn: true, audioOn: true },
  { id: '2', name: 'Maya Patel', avatar: 'MP', isSpeaking: false, videoOn: true, audioOn: true },
  { id: '3', name: 'Carlos Ruiz', avatar: 'CR', isSpeaking: false, videoOn: false, audioOn: true },
  { id: '4', name: 'Yuki Tanaka', avatar: 'YT', isSpeaking: false, videoOn: true, audioOn: false },
]

const CHAT_MESSAGES: ChatMessage[] = [
  { id: '1', sender: 'Maya Patel', text: 'Can everyone see my screen?', time: '2:31 PM' },
  { id: '2', sender: 'Carlos Ruiz', text: 'Yes, looks good!', time: '2:31 PM' },
  { id: '3', sender: 'Yuki Tanaka', text: 'The new dashboard looks amazing', time: '2:32 PM' },
]

type Mode = 'video' | 'voice' | 'chat'

export default function MeetingRoom() {
  const [mode, setMode] = useState<Mode>('video')
  const [videoOn, setVideoOn] = useState(true)
  const [audioOn, setAudioOn] = useState(true)
  const [showChat, setShowChat] = useState(false)
  const [messages, setMessages] = useState(CHAT_MESSAGES)
  const [newMessage, setNewMessage] = useState('')
  const [inCall, setInCall] = useState(false)

  const sendMessage = () => {
    if (!newMessage.trim()) return
    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      sender: 'Alex Chen',
      text: newMessage,
      time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    }])
    setNewMessage('')
  }

  if (!inCall) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[600px] space-y-8">
        <div className="text-center">
          <h1 className="text-2xl font-light text-white mb-2">Meeting Room</h1>
          <p className="text-gray-500">Start or join a meeting</p>
        </div>

        {/* Mode Selection */}
        <div className="flex gap-4">
          {[
            { key: 'video', icon: Video, label: 'Video call' },
            { key: 'voice', icon: Mic, label: 'Voice only' },
            { key: 'chat', icon: MessageSquare, label: 'Chat room' },
          ].map(m => (
            <button
              key={m.key}
              onClick={() => setMode(m.key as Mode)}
              className={cn(
                'flex flex-col items-center gap-3 p-6 rounded-2xl transition-all',
                mode === m.key
                  ? 'bg-lambda-gold/20 ring-1 ring-lambda-gold/50'
                  : 'bg-white/5 hover:bg-white/10'
              )}
            >
              <m.icon className={cn(
                'w-8 h-8',
                mode === m.key ? 'text-lambda-gold' : 'text-gray-400'
              )} />
              <span className={cn(
                'text-sm',
                mode === m.key ? 'text-white' : 'text-gray-500'
              )}>
                {m.label}
              </span>
            </button>
          ))}
        </div>

        {/* Preview */}
        <div className="w-80 aspect-video bg-black/50 rounded-2xl overflow-hidden relative">
          {mode !== 'chat' ? (
            <>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-lambda-gold/20 flex items-center justify-center">
                  <span className="text-lambda-gold text-2xl font-medium">AC</span>
                </div>
              </div>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                <button
                  onClick={() => setVideoOn(!videoOn)}
                  className={cn(
                    'p-3 rounded-full transition-colors',
                    videoOn ? 'bg-white/10' : 'bg-red-500'
                  )}
                >
                  {videoOn ? <Video className="w-5 h-5 text-white" /> : <VideoOff className="w-5 h-5 text-white" />}
                </button>
                <button
                  onClick={() => setAudioOn(!audioOn)}
                  className={cn(
                    'p-3 rounded-full transition-colors',
                    audioOn ? 'bg-white/10' : 'bg-red-500'
                  )}
                >
                  {audioOn ? <Mic className="w-5 h-5 text-white" /> : <MicOff className="w-5 h-5 text-white" />}
                </button>
              </div>
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <MessageSquare className="w-16 h-16 text-gray-600" />
            </div>
          )}
        </div>

        {/* Start Button */}
        <button
          onClick={() => setInCall(true)}
          className="px-8 py-3 bg-lambda-gold text-black rounded-full font-medium hover:bg-lambda-gold/90 transition-colors"
        >
          {mode === 'chat' ? 'Join chat' : 'Start meeting'}
        </button>
      </div>
    )
  }

  return (
    <div className="flex h-[calc(100vh-120px)] gap-4">
      {/* Main Area */}
      <div className="flex-1 flex flex-col">
        {/* Video Grid / Voice Grid / Chat */}
        <div className="flex-1 relative">
          {mode === 'chat' ? (
            <ChatView messages={messages} />
          ) : (
            <div className="grid grid-cols-2 gap-4 h-full">
              {PARTICIPANTS.map(p => (
                <div
                  key={p.id}
                  className={cn(
                    'relative rounded-2xl overflow-hidden',
                    p.videoOn ? 'bg-gray-800' : 'bg-black/50'
                  )}
                >
                  {p.videoOn && mode === 'video' ? (
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-700 to-gray-900" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className={cn(
                        'w-20 h-20 rounded-full flex items-center justify-center',
                        p.isSpeaking ? 'bg-lambda-gold/30 ring-2 ring-lambda-gold' : 'bg-white/10'
                      )}>
                        <span className="text-white text-xl font-medium">{p.avatar}</span>
                      </div>
                    </div>
                  )}
                  {/* Name */}
                  <div className="absolute bottom-3 left-3 flex items-center gap-2">
                    <span className="text-white text-sm">{p.name}</span>
                    {!p.audioOn && <MicOff className="w-3 h-3 text-red-400" />}
                  </div>
                  {/* Speaking indicator */}
                  {p.isSpeaking && (
                    <div className="absolute top-3 right-3">
                      <span className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-3 py-6">
          {mode !== 'chat' && (
            <>
              <button
                onClick={() => setAudioOn(!audioOn)}
                className={cn(
                  'p-4 rounded-full transition-colors',
                  audioOn ? 'bg-white/10 hover:bg-white/20' : 'bg-red-500'
                )}
              >
                {audioOn ? <Mic className="w-5 h-5 text-white" /> : <MicOff className="w-5 h-5 text-white" />}
              </button>
              {mode === 'video' && (
                <button
                  onClick={() => setVideoOn(!videoOn)}
                  className={cn(
                    'p-4 rounded-full transition-colors',
                    videoOn ? 'bg-white/10 hover:bg-white/20' : 'bg-red-500'
                  )}
                >
                  {videoOn ? <Video className="w-5 h-5 text-white" /> : <VideoOff className="w-5 h-5 text-white" />}
                </button>
              )}
              <button className="p-4 rounded-full bg-white/10 hover:bg-white/20 transition-colors">
                <ScreenShare className="w-5 h-5 text-white" />
              </button>
            </>
          )}
          <button
            onClick={() => setInCall(false)}
            className="p-4 rounded-full bg-red-500 hover:bg-red-600 transition-colors"
          >
            <Phone className="w-5 h-5 text-white rotate-[135deg]" />
          </button>
          {mode !== 'chat' && (
            <button
              onClick={() => setShowChat(!showChat)}
              className={cn(
                'p-4 rounded-full transition-colors',
                showChat ? 'bg-lambda-gold/20 text-lambda-gold' : 'bg-white/10 hover:bg-white/20 text-white'
              )}
            >
              <MessageSquare className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Chat Sidebar */}
      {(showChat || mode === 'chat') && mode !== 'chat' && (
        <div className="w-80 bg-white/5 rounded-2xl flex flex-col">
          <div className="p-4 border-b border-white/5">
            <h3 className="text-white font-medium">Chat</h3>
          </div>
          <ChatView messages={messages} />
          <ChatInput value={newMessage} onChange={setNewMessage} onSend={sendMessage} />
        </div>
      )}

      {/* Full Chat Input for chat mode */}
      {mode === 'chat' && (
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-[#0a0a0f]">
          <ChatInput value={newMessage} onChange={setNewMessage} onSend={sendMessage} />
        </div>
      )}
    </div>
  )
}

function ChatView({ messages }: { messages: ChatMessage[] }) {
  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      {messages.map(msg => (
        <div key={msg.id}>
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-medium text-white">{msg.sender}</span>
            <span className="text-xs text-gray-600">{msg.time}</span>
          </div>
          <p className="text-gray-300 text-sm mt-1">{msg.text}</p>
        </div>
      ))}
    </div>
  )
}

function ChatInput({ value, onChange, onSend }: { value: string; onChange: (v: string) => void; onSend: () => void }) {
  return (
    <div className="p-4 border-t border-white/5">
      <div className="flex gap-2">
        <input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && onSend()}
          placeholder="Type a message..."
          className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white placeholder-gray-500 outline-none"
        />
        <button
          onClick={onSend}
          className="p-2 bg-lambda-gold text-black rounded-lg hover:bg-lambda-gold/90 transition-colors"
        >
          <Send className="w-5 h-5" />
        </button>
      </div>
    </div>
  )
}
